import React, { PureComponent } from 'react';
import { intlShape } from 'react-intl';
import PropTypes from 'prop-types';

import SimpleButton from '@components/SimpleButton';
import ShellFooter from '@components/ShellFooter';
import Shell from '@shared/containers/Shell';

import { isNullOrUndefined } from '@utils/object';
import cleanNotAvailableOptions from '@productConfig/utils/cleanNotAvailableOptions';
import getNotSubmittedOptions from '@productConfig/utils/getNotSubmittedOptions';
import { ERROR as TRACKING_ERROR } from '@productConfig/tracking/constants';
import QuantityCounter from '@productConfig/components/QuantityCounter';
import Header from '@productConfig/components/Header';
import { BUSINESS_TYPE } from '@commons/constants';
import Modal from '@pedidosya/web-fenix/system/Modal';
import Button from '@pedidosya/web-fenix/system/Button';
import LocationParcel from '@location/containers/LocationParcel';
import ProductOptionList from './components/ProductOptionList';
import ProductOptionGroups from './components/ProductOptionGroups';
import OptionsGroupShimmer from './components/OptionsGroupShimmer';
import Notes from './components/Notes';
import NutritionalTable from './components/NutritionalTable';

import ProductQuantity from './components/ProductQuantity';

import messages from './messages';
import connect from './connect';
import { ProductConfigurationFooter } from './components/ProductConfigurationFooter';

const NOTES_FOR_RESTAURANT = {
  [BUSINESS_TYPE.RESTAURANT]: {
    subtitle: messages.clarification_subtitle_restaurant_coffee,
    description: messages.clarification_restaurant_description,
    label: messages.clarification_input_label_restaurant,
  },
};

class ProductConfiguration extends PureComponent {
  productOptionListInitValues = {
    isShowingModal: false,
    option: null,
  };

  constructor(props) {
    super(props);

    const { cartItemInEdition } = props;

    this.state = {
      crossSellingProducts: [],
      initialQuantity: cartItemInEdition?.quantity || 0,
      isAddingLine: false,
      isEditMode: Boolean(cartItemInEdition),
      notes: cartItemInEdition?.notes || '',
      optionGroups: cleanNotAvailableOptions(cartItemInEdition?.optionGroups),
      productOptionList: this.productOptionListInitValues,
      quantity: cartItemInEdition?.quantity || 1,
      showLocationParcel: false,
    };
  }

  async componentDidMount() {
    const {
      cartItemInEdition,
      fetchProductConfig,
      shop,
      productId,
      trackProductConfiguration,
      fetchVendorNotesConfig,
      product,
      isQC,
      fetchProductOptions,
    } = this.props;

    const { quantity } = this.state;
    if ((!cartItemInEdition && !productId) || !shop) {
      const paramsValues = `cartItem: ${cartItemInEdition} | productId: ${productId} | shop: ${!!shop}`;
      throw new Error(
        `Product Configuration must be provided with a productId or cartItemInEdition props | ${paramsValues}`,
      );
    }

    const id = cartItemInEdition?.product || productId;

    const payload = {
      productId: id,
      shopId: shop.id,
      isFavourite: product?.isFavourite || false,
      itemId: product?.itemId || null,
      subsidizedProduct: product?.subsidizedProduct || null,
      price: product?.price || null,
      discount: product?.discount || null,
    };

    if (isQC) {
      await fetchProductOptions(payload);
    } else {
      await fetchProductConfig(payload);
    }

    await fetchVendorNotesConfig();

    trackProductConfiguration({ quantity });
  }

  componentDidUpdate(prevProps) {
    const { product } = this.props;
    if (prevProps.product !== product) {
      this.checkProductQuantity();
    }
  }

  componentWillUnmount() {
    const { resetModule } = this.props;
    resetModule();
  }

  getCrossSellingProducts = async () => {
    const { shop, fetchProductCrossSelling, product } = this.props;

    const id = product?.itemId;
    if (id) {
      const { products } = await fetchProductCrossSelling(shop.id, id);
      this.setState({
        crossSellingProducts: products || [],
      });
    }
  };

  updateProducts = newProducts => {
    this.setState({
      crossSellingProducts: newProducts || [],
    });
  };

  onNotesChange = notes => this.setState({ notes });

  showListOptions = option => () =>
    this.setState({ productOptionList: { isShowingModal: true, option } });

  closeListOptions = () =>
    this.setState({ productOptionList: this.productOptionListInitValues });

  onConfirmOptionGroup = optionGroup => {
    this.setState(state => {
      const optionGroups = state.optionGroups.filter(
        x => x.id !== optionGroup.id,
      );

      optionGroups.push(optionGroup);

      return {
        optionGroups,
        productOptionList: this.productOptionListInitValues,
      };
    });
  };

  isAddMode = () => {
    const { cartItemInEdition } = this.props;
    return isNullOrUndefined(cartItemInEdition);
  };

  onValidateAge = validated => {
    if (validated) this.onSubmit();
    else this.onFinish();
  };

  getCrossSellingItemsToSave = crossSellingProducts =>
    crossSellingProducts
      .filter(item => item.quantity > 0)
      .map(item => ({
        quantity: item.quantity,
        product: item.id,
        optionGroups: [],
        notes: null,
        requiresAgeCheck: false,
      }));

  onSubmit = () => {
    const {
      product,
      addLine,
      addLines,
      updateLine,
      cartItemInEdition,
      trackProductConfiguration,
      openAgeValidation,
      hasToValidateAge,
      deleteLines,
    } = this.props;

    if (product.requiresAgeCheck && hasToValidateAge) {
      openAgeValidation({ onValidate: this.onValidateAge });
      return;
    }

    if (!this.checkProductQuantity()) return;

    const { optionGroups, quantity, notes, crossSellingProducts } = this.state;

    const notSubmittedOptions = getNotSubmittedOptions(product, optionGroups);

    if (notSubmittedOptions?.length > 0) {
      this.showListOptions(notSubmittedOptions[0])();
      trackProductConfiguration({
        quantity,
        errorType: TRACKING_ERROR.MANDATORY_OPTION_MISSING,
      });
    } else {
      const line = {
        quantity,
        product: product.id,
        optionGroups,
        notes,
        requiresAgeCheck: product.requiresAgeCheck,
      };
      if (quantity > 0) {
        this.setState({ isAddingLine: true }, () => {
          if (this.isAddMode()) {
            const confirmLine = addLine({ line });
            confirmLine.finally(() => {
              this.onFinish();
              const crossSellingItems = this.getCrossSellingItemsToSave(
                crossSellingProducts,
              );
              addLines({
                lines: crossSellingItems || [],
              });
            });
          } else {
            const confirmLine = updateLine({
              line: { ...line, guid: cartItemInEdition.guid },
            });
            confirmLine.finally(() => {
              this.onFinish();
            });
          }
        });
      }
      if (quantity === 0 && cartItemInEdition) {
        const lines = [cartItemInEdition.guid];
        this.setState({ isAddingLine: true }, () => {
          deleteLines({ lines, origin: 'product_configuration' }).finally(
            this.onFinish,
          );
        });
      }
    }
  };

  getTextForOption = id => {
    const { optionGroups } = this.state;
    const { product } = this.props;

    const submittedOption = optionGroups?.find(x => x.id === id);
    if (!submittedOption) return '';

    const option = product.optionGroups?.find(x => x.id === submittedOption.id);

    const selectedOptions =
      option.options?.filter(x =>
        submittedOption.options?.some(y => y.id === x.id),
      ) || [];

    const text = selectedOptions.map(x => x.name).join(', ');
    return text;
  };

  showNeutralToast = message => {
    const {
      showNeutralToast,
      intl: { formatMessage },
    } = this.props;
    showNeutralToast({ title: formatMessage(message) });
  };

  checkProductQuantity() {
    const {
      productQuantity,
      cartTotalProductsQuantity,
      product,
      shopMaxProducts,
    } = this.props;
    const { isEditMode, quantity } = this.state;
    const qt = isEditMode || shopMaxProducts > 0 ? quantity : productQuantity;
    if (shopMaxProducts > 0) {
      const { initialQuantity } = this.state;
      const itemsQuantity =
        cartTotalProductsQuantity + quantity - initialQuantity;

      if (itemsQuantity > shopMaxProducts) {
        this.showNeutralToast(messages.shop_limit_overcomed);
        return false;
      }

      const shopLimitOvercomedWithThis = quantity > shopMaxProducts;
      if (shopLimitOvercomedWithThis && !isEditMode) {
        this.showNeutralToast(messages.shop_limit_overcomed);
        return false;
      }

      if (!isEditMode && productQuantity >= product?.maxQuantity) {
        this.showNeutralToast(messages.shop_limit_overcomed);
        return false;
      }
    } else if (!isEditMode && product?.maxQuantity === qt) {
      this.showNeutralToast(messages.product_limit_value_overcomed);
      return false;
    }
    return true;
  }

  changeLimitQuantity = quantity => {
    const {
      product,
      productQuantity,
      cartItemInEdition,
      shopMaxProducts,
      cartTotalProductsQuantity,
    } = this.props;

    // eslint-disable-next-line react/destructuring-assignment
    const isAdding = quantity >= this.state.quantity;
    const conunterMin = cartItemInEdition ? 0 : 1;

    const productHasOvercomedLimit = value => value > product.maxQuantity;
    const hasOvercomedShopLimit =
      Math.abs(productQuantity - (cartTotalProductsQuantity + quantity)) >
      shopMaxProducts;

    if (isAdding && product.maxQuantity) {
      const productIsOvercomingLimit = productHasOvercomedLimit(
        Math.abs(productQuantity - (cartItemInEdition?.quantity || 0)) +
          quantity,
      );
      if (quantity > 0 && !productIsOvercomingLimit) {
        this.setState({ quantity });
      } else {
        this.showNeutralToast(messages.product_limit_value_overcomed);
      }
    } else if (isAdding && hasOvercomedShopLimit) {
      this.showNeutralToast(messages.shop_limit_overcomed);
    } else if (productHasOvercomedLimit(quantity)) {
      this.showNeutralToast(
        isAdding
          ? messages.product_limit_value_overcomed
          : messages.product_overcoming_limit_value,
      );
    } else if (quantity >= conunterMin) {
      this.setState({ quantity });
    }
  };

  changeQuantity = quantity => {
    const { cartItemInEdition } = this.props;
    const max = cartItemInEdition ? 0 : 1;
    if (quantity >= max) this.setState({ quantity });
  };

  onFinish = () => {
    const {
      redirect,
      shop,
      redirectToShopDetails,
      goBack,
      deepLink,
      isNotOrganicFlow,
    } = this.props;

    if (redirectToShopDetails || isNotOrganicFlow || deepLink?.from)
      redirect(shop.url);
    else goBack();
  };

  isLoading() {
    const { hasCart, product } = this.props;
    return Boolean(!hasCart || product?.loading);
  }

  onGetPromiseLocation = async () => {
    if (this.resolveFlow) {
      this.cleanupLocationFlow();
    }

    this.setState({ showLocationParcel: true });
    return new Promise((resolve, reject) => {
      this.resolveFlow = resolve;
      this.rejectFlow = reject;
    });
  };

  cleanupLocationFlow = () => {
    if (this.resolveFlow) {
      this.resolveFlow(null);
    }
    this.resolveFlow = null;
    this.rejectFlow = null;
    this.setState({ showLocationParcel: false });
  };

  onFinishLocation = async location => {
    if (!this.resolveFlow) return;

    try {
      if (!location) {
        this.resolveFlow(null);
        return;
      }

      const { fetchProductNearUser } = this.props;
      const { latitude, longitude } = location;
      await fetchProductNearUser({ latitude, longitude });
      this.resolveFlow(location);
    } catch (error) {
      this.resolveFlow(null);
    } finally {
      this.cleanupLocationFlow();
    }
  };

  onCancelParcel = () => {
    this.cleanupLocationFlow();
  };

  render() {
    const {
      intl: { formatMessage },
      product,
      Footer,
      nutritionInfo,
      pushPage,
      cartItemInEdition,
      withOverlay,
      showNotes,
      shopBusinessType,
      isQC,
      isNotOrganicFlow,
    } = this.props;

    const {
      notes,
      productOptionList,
      optionGroups,
      isAddingLine,
      quantity,
      crossSellingProducts,
      showLocationParcel,
    } = this.state;

    if (showLocationParcel) {
      return (
        <LocationParcel
          show={showLocationParcel}
          onSuccess={this.onFinishLocation}
          onCancel={this.onCancelParcel}
        />
      );
    }

    if (productOptionList.isShowingModal) {
      return (
        <ProductOptionList
          optionGroup={productOptionList.option}
          initialValue={optionGroups.find(
            x => x.id === productOptionList.option.id,
          )}
          onClose={this.closeListOptions}
          onConfirm={this.onConfirmOptionGroup}
          Footer={Footer}
        />
      );
    }

    const customNote = NOTES_FOR_RESTAURANT[shopBusinessType];

    if (!isQC && !!product && !product?.isAvailable && !product?.loading) {
      return (
        <Modal
          open
          title={formatMessage(messages.button_product_not_available_title)}
          text={formatMessage(messages.button_product_not_available_content)}
          primaryButton={
            <Button
              label={formatMessage(messages.button_product_not_available_ok)}
              size="large"
              fullWidth
              onClick={this.onFinish}
            />
          }
        />
      );
    }

    return (
      <Shell
        showOverlaySpinner={isAddingLine || !product}
        isModal
        header={
          product && (
            <Header
              product={product}
              onClose={this.onFinish}
              withOverlay={withOverlay}
              nutritionInfo={nutritionInfo}
              isQC={isQC}
            />
          )
        }
        footer={
          product && (
            <ShellFooter>
              <ProductConfigurationFooter
                cartItemInEdition={cartItemInEdition}
                changeLimitQuantity={this.changeLimitQuantity}
                changeQuantity={this.changeQuantity}
                crossSellingProducts={crossSellingProducts}
                Footer={Footer}
                isLoading={this.isLoading()}
                isNotOrganicFlow={isNotOrganicFlow}
                onGetPromiseLocation={this.onGetPromiseLocation}
                onSubmit={this.onSubmit}
                product={product}
                quantity={quantity}
              />
            </ShellFooter>
          )
        }
      >
        {product && (
          <>
            {product.loading ? (
              <OptionsGroupShimmer />
            ) : (
              <>
                <ProductOptionGroups
                  optionGroups={product.optionGroups}
                  onGetTextForOption={this.getTextForOption}
                  onShowListOptions={this.showListOptions}
                />
                {nutritionInfo?.backLabels && (
                  <NutritionalTable
                    pushPage={pushPage}
                    gtin={product.integrationCode}
                  />
                )}
                <ProductQuantity productMaxQuantity={product.maxQuantity}>
                  <QuantityCounter
                    quantity={quantity}
                    productMaxQuantity={product.maxQuantity}
                    onChangeLimitQuantity={this.changeLimitQuantity}
                    onChangeQuantity={this.changeQuantity}
                  />
                </ProductQuantity>
                {showNotes && (
                  <Notes
                    subtitle={formatMessage(
                      customNote?.subtitle || messages.clarification_subtitle,
                    )}
                    description={
                      customNote?.description
                        ? formatMessage(customNote?.description)
                        : null
                    }
                    notes={notes}
                    onChange={this.onNotesChange}
                    placeholder={formatMessage(
                      customNote?.label || messages.clarification_input_label,
                    )}
                  />
                )}
              </>
            )}
          </>
        )}
      </Shell>
    );
  }
}

ProductConfiguration.propTypes = {
  // data
  intl: intlShape.isRequired,
  product: PropTypes.object.isRequired,
  shop: PropTypes.shape({
    id: PropTypes.number,
    url: PropTypes.string,
  }),
  productQuantity: PropTypes.number,
  cartTotalProductsQuantity: PropTypes.number,
  shopMaxProducts: PropTypes.number,
  cartItemInEdition: PropTypes.shape({
    guid: PropTypes.string,
    product: PropTypes.number,
    quantity: PropTypes.number,
    notes: PropTypes.string,
    optionGroups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        options: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.number,
          }),
        ),
      }),
    ),
  }),
  productId: PropTypes.string,
  withOverlay: PropTypes.bool.isRequired,
  isQC: PropTypes.bool.isRequired,
  redirectToShopDetails: PropTypes.bool.isRequired,
  hasCart: PropTypes.bool.isRequired,
  nutritionInfo: PropTypes.array,
  showNotes: PropTypes.bool,
  shopBusinessType: PropTypes.string,

  // WithAgeValidation
  hasToValidateAge: PropTypes.bool.isRequired,
  openAgeValidation: PropTypes.func.isRequired,

  // callbacks
  addLine: PropTypes.func.isRequired,
  addLines: PropTypes.func.isRequired,
  updateLine: PropTypes.func.isRequired,
  deleteLines: PropTypes.func.isRequired,
  goBack: PropTypes.func.isRequired,
  redirect: PropTypes.func.isRequired,
  resetModule: PropTypes.func.isRequired,
  showNeutralToast: PropTypes.func.isRequired,
  trackProductConfiguration: PropTypes.func.isRequired,
  pushPage: PropTypes.func.isRequired,
  fetchVendorNotesConfig: PropTypes.func.isRequired,
  fetchProductCrossSelling: PropTypes.func,
  fetchProductConfig: PropTypes.func.isRequired,
  fetchProductOptions: PropTypes.func.isRequired,
  fetchProductNearUser: PropTypes.func.isRequired,

  // Joker
  Footer: PropTypes.object,

  // Deeplink
  isNotOrganicFlow: PropTypes.bool,
  deepLink: PropTypes.shape({
    from: PropTypes.string,
  }),
};

ProductConfiguration.defaultProps = {
  Footer: SimpleButton,
};

export { ProductConfiguration };
export default connect(ProductConfiguration);
